import { verseById } from "../data/verses";
import {
  countsFromIds,
  expandCounts,
  ownedCount,
  totalCount,
  type CardCounts,
} from "./counts";
import { missionById } from "./missions";
import type { CampaignSave, MissionDef } from "./types";

function validVerse(id: string): boolean {
  return !!verseById(id);
}

/**
 * Resolve the run deck for a mission from policy + collection.
 * Returns an expanded id list (multiplicity = repeated ids).
 * Fixed hands may include unowned "borrowed" teaching cards.
 * Pool mode uses owned copies (all stacks), topped up from core if undersized.
 */
export function resolveMissionLoadout(
  mission: MissionDef,
  save: CampaignSave,
): string[] {
  const owned = save.ownedCounts;

  if (mission.loadout.mode === "fixed") {
    return expandCounts(
      countsFromIds(mission.loadout.core.filter(validVerse)),
    );
  }

  if (mission.loadout.mode === "fixed+carryIn") {
    const counts: CardCounts = countsFromIds(
      mission.loadout.core.filter(validVerse),
    );
    const fromId = mission.loadout.carryInFromMissionId;
    const prev = fromId ? missionById(fromId) : undefined;
    if (prev) {
      const max = mission.loadout.maxCarryIn ?? prev.rewardCards.length;
      let added = 0;
      for (const id of prev.rewardCards) {
        if (added >= max) break;
        if (!validVerse(id) || ownedCount(owned, id) < 1) continue;
        // Carry-in at least one owned copy (don't force full stack into teaching hand)
        if (ownedCount(counts, id) < 1) {
          counts[id] = 1;
          added++;
        }
      }
    }
    return expandCounts(counts);
  }

  // pool — use player's deckCounts clamped to owned, else all owned copies
  const min =
    mission.loadout.minPoolSize ?? Math.max(6, mission.loadout.core.length);

  let deck: CardCounts = {};
  const source =
    totalCount(save.deckCounts) > 0 ? save.deckCounts : save.ownedCounts;
  for (const [id, n] of Object.entries(source)) {
    if (!validVerse(id)) continue;
    const have = ownedCount(owned, id);
    if (have < 1) continue;
    deck[id] = Math.min(Math.floor(n), have);
  }

  // Top up with owned extras if under min size
  if (totalCount(deck) < min) {
    for (const [id, n] of Object.entries(owned)) {
      if (!validVerse(id)) continue;
      const cur = ownedCount(deck, id);
      const room = n - cur;
      if (room > 0) {
        deck[id] = cur + room;
      }
      if (totalCount(deck) >= min) break;
    }
  }

  // Fallback: borrow core teaching cards
  if (totalCount(deck) < min) {
    for (const id of mission.loadout.core) {
      if (!validVerse(id)) continue;
      if (ownedCount(deck, id) < 1) deck[id] = 1;
      if (totalCount(deck) >= min) break;
    }
  }

  if (totalCount(deck) === 0) {
    return expandCounts(
      countsFromIds(
        mission.loadout.core.filter(validVerse).length
          ? mission.loadout.core.filter(validVerse)
          : ["heb-11-1"],
      ),
    );
  }
  return expandCounts(deck);
}

export function loadoutLabel(mission: MissionDef, deck: string[]): string {
  const counts = countsFromIds(deck);
  const kinds = Object.keys(counts).length;
  switch (mission.loadout.mode) {
    case "fixed":
      return `Teaching deck · ${deck.length} cards (${kinds} verses)`;
    case "fixed+carryIn":
      return `Teaching deck + carry-in · ${deck.length} cards`;
    case "pool":
      return `Your deck · ${deck.length} cards (${kinds} verses)`;
  }
}

/** Summarize multiplicity for UI: "Heb 11:1 ×2". */
export function countLabel(id: string, n: number, reference?: string): string {
  const name = reference ?? id;
  return n > 1 ? `${name} ×${n}` : name;
}
